"use client";

/**
 * 사용자 가이드 — v4 (2026-06-02).
 * v3 (5/11) → v4: 직원·세금·상권·트렌드 파이프라인 신규 영역 반영.
 * 지표 갱신 (530 키워드 / 50 공급처 / 51,587 제품 / 340,408 카페).
 *
 * 진입점: /guide (마이페이지 링크), MMS 캠페인 직접 도달.
 * PDF: print() 호출 → 브라우저 내장 "PDF 로 저장".
 */
import Link from "next/link";

export default function GuidePage() {
  const handlePrintPdf = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <div className="pb-24">
      {/* 헤더 */}
      <div className="sticky top-0 z-40 bg-bg/90 backdrop-blur-md border-b border-border px-4 py-3 flex items-center gap-3 print:static print:bg-transparent">
        <Link href="/mypage" className="text-text-sub hover:text-text transition-colors print:hidden">
          ←
        </Link>
        <h1 className="text-base font-black text-text">📖 사용 가이드</h1>
        <button
          onClick={handlePrintPdf}
          className="ml-auto text-2xs px-3 py-1.5 rounded-lg border border-border hover:bg-bg-elevated transition-colors print:hidden"
        >
          PDF 다운로드
        </button>
      </div>

      <div className="px-4 py-5 space-y-3 print:px-8 print:py-6">
        {/* 표지 */}
        <section className="card text-center print:break-after-page">
          <h2 className="text-2xl font-black text-primary">트렌드레인</h2>
          <p className="text-xs text-text-sub mt-1.5">카페 사장님 한 명을 위한 통합 운영 SaaS</p>
          <p className="mt-2 text-2xs text-text-sub">
            활성 키워드 <b className="text-text">530</b> · 공급처 <b className="text-text">50</b> ·
            제품 <b className="text-text">51,587</b> · 전국 카페 <b className="text-text">340,408</b> 추적 중
          </p>
        </section>

        {/* 1. 30초 가입 → 5분 셋업 */}
        <section className="card">
          <h3 className="text-sm font-bold text-text mb-3">🚀 30초 가입 → 5분 셋업</h3>
          <ol className="space-y-2 text-xs text-text leading-relaxed">
            <li className="flex gap-2.5 items-start">
              <span className="shrink-0 w-5 h-5 rounded-full bg-primary/15 text-primary text-2xs font-bold flex items-center justify-center mt-0.5">1</span>
              <span>카카오로 1초 가입 <span className="text-text-sub">(베타 기간 무료)</span></span>
            </li>
            <li className="flex gap-2.5 items-start">
              <span className="shrink-0 w-5 h-5 rounded-full bg-primary/15 text-primary text-2xs font-bold flex items-center justify-center mt-0.5">2</span>
              <span>매장 이름·주소 등록 <span className="text-text-sub">(1분)</span></span>
            </li>
            <li className="flex gap-2.5 items-start">
              <span className="shrink-0 w-5 h-5 rounded-full bg-primary/15 text-primary text-2xs font-bold flex items-center justify-center mt-0.5">3</span>
              <span>재료·메뉴·레시피 입력 → 매출 입력만으로 손익 자동 집계</span>
            </li>
          </ol>
          <p className="mt-2.5 text-2xs font-semibold text-primary">→ 첫날부터 매출·원가·BEP·메뉴 ROI 가 보입니다.</p>
        </section>

        {/* 2. 핵심 기능 8개 */}
        <section className="card">
          <h3 className="text-sm font-bold text-text mb-3">🎯 핵심 기능</h3>
          <div className="space-y-2.5">
            {[
              { num: "①", emoji: "🌧", title: "라이프사이클 트렌드", desc: "530+ 활성 키워드 · 4단계 (태동/성장/성숙/쇠퇴) 자동 분류 · 매일 06:00 알림", link: "/", linkLabel: "홈" },
              { num: "②", emoji: "🏪", title: "매장 매출·원가·손익", desc: "매출 1번 입력 → 원가·총이익·BEP·메뉴 ROI 자동 계산", link: "/store/dashboard", linkLabel: "매장" },
              { num: "③", emoji: "📑", title: "세금 자동 (부가세·종소세·세액공제)", desc: "두루누리·통합고용·의제매입·신용카드 세액공제 자동 매칭 + 신고서 다운로드", link: "/store/tax", linkLabel: "세금" },
              { num: "④", emoji: "👥", title: "직원 관리·급여 자동", desc: "4대보험·간이세액·주휴수당·근로기준법 가드 + 급여명세서 PDF", link: "/store/staff", linkLabel: "직원" },
              { num: "⑤", emoji: "🏭", title: "공급처 통합 검색", desc: "50개 제조사 · 51,587개 제품 매칭 + 외부 발주 link", link: "/manufacturers", linkLabel: "공급처" },
              { num: "⑥", emoji: "🗺", title: "상권 분석", desc: "반경 1km/3km/5km 카페 분포 + 신규/폐업 6개월 추이 + 임대 시세", link: "/map", linkLabel: "지도" },
              { num: "⑦", emoji: "🎨", title: "AI 이미지 스튜디오", desc: "사진 1장 + 프롬프트 → 메뉴판·SNS 카드뉴스 1초", link: "/image-studio", linkLabel: "AI" },
              { num: "⑧", emoji: "🤖", title: "AI 비서", desc: "자연어 질문 → 매장 데이터 자동 조회·계산·인사이트", link: "/chat", linkLabel: "비서" },
            ].map((f) => (
              <Link
                key={f.num}
                href={f.link}
                className="flex items-center gap-2.5 py-1.5 px-2 -mx-2 rounded-lg hover:bg-bg-elevated transition-colors print:hover:bg-transparent"
              >
                <span className="text-2xs font-black text-primary w-4 shrink-0">{f.num}</span>
                <span className="text-base shrink-0">{f.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-text">{f.title}</p>
                  <p className="text-2xs text-text-sub leading-snug">{f.desc}</p>
                </div>
                <span className="text-2xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary shrink-0 print:hidden">
                  {f.linkLabel} →
                </span>
              </Link>
            ))}
          </div>
          <p className="mt-3 text-2xs text-text-sub text-center">
            💡 막힐 때는 <b className="text-text">🤖 AI 비서</b> 에 자연어로 물어보세요
          </p>
        </section>

        {/* 3. 신기능 하이라이트 */}
        <section className="card border-primary/30 bg-primary/5 print:bg-transparent print:border-gray-300">
          <h3 className="text-sm font-bold text-primary mb-3">✨ 신규·강조 기능</h3>
          <ul className="space-y-2 text-xs text-text leading-relaxed">
            <li><b className="text-text">💰 세무사 기장료 절감</b> — 매월 10~20만원 → 자동 분류·신고서 다운로드</li>
            <li><b className="text-text">📷 영수증 OCR</b> — 세금 영역에서 사진 1장 → 거래처·금액 1초 자동 입력</li>
            <li><b className="text-text">👥 노무사 외주 절감</b> — 직원 급여·4대보험·간이세액 자동 + 근로기준법 위반 알람</li>
            <li><b className="text-text">🗺 상권 분석</b> — 부동산·컨설팅 100만원+ 자료를 무료 공공 데이터로 한 화면</li>
            <li><b className="text-text">🌧 트렌드 자율 파이프라인</b> — 4채널 (블로그·카페·인스타·틱톡) 매일 수집 · AI 정제 · 라이프사이클 분류</li>
            <li><b className="text-text">🔔 텔레그램 자동 알림</b> — 06:00 트렌드 / 09:00 KPI / 멘션 / 재고 critical</li>
          </ul>
        </section>

        {/* 4. 자주 묻는 질문 */}
        <section className="card">
          <h3 className="text-sm font-bold text-text mb-3">❓ 자주 묻는 질문</h3>
          <div className="space-y-2.5">
            <div>
              <p className="text-xs font-semibold text-text">Q. 베타 기간 비용은?</p>
              <p className="mt-0.5 text-2xs text-text-sub leading-relaxed">
                현재 모든 기능 무료. 베타 종료 후 월 3,900원 (월간 결제만). 가맹 본사는 별도 협의.
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-text">Q. 매장 여러 개 관리되나요?</p>
              <p className="mt-0.5 text-2xs text-text-sub leading-relaxed">
                한 계정에서 여러 매장 등록·전환 가능. 각 매장 손익·세금·직원 별도 운영.
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-text">Q. 세금 신고서 그대로 제출 가능?</p>
              <p className="mt-0.5 text-2xs text-text-sub leading-relaxed">
                자동 집계·분류는 우리가 해주지만 최종 제출은 사장님 또는 세무사 검토 후 권장.
                세액공제 자격 자동 매칭은 신뢰성 확인 차원에서 자세한 사유까지 표시.
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-text">Q. 직원 급여 계산은 노무사 수준?</p>
              <p className="mt-0.5 text-2xs text-text-sub leading-relaxed">
                4대보험·간이세액·주휴수당·두루누리 등 표준 룰 자동 적용. 특수 케이스 (외국인·연차수당·퇴직금)
                는 노무사 검토 권장.
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-text">Q. 영수증 OCR 이 안 보이는데?</p>
              <p className="mt-0.5 text-2xs text-text-sub leading-relaxed">
                현재 OCR 은 세금 영역에서만 사용. 입고 영역은 CSV 또는 직접 입력.
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-text">Q. 가맹 본사로 가입할 수 있나요?</p>
              <p className="mt-0.5 text-2xs text-text-sub leading-relaxed">
                네. 마이페이지 → 본사 신청 → 운영자 승인 → 1회용 코드로 가맹점 자동 가입.
                본사 대시보드에서 가맹점 매출·원가·KPI 통합 관리.
              </p>
            </div>
            <p className="text-2xs text-text-sub pt-1 border-t border-border">
              그 외 질문은 <b className="text-text">🤖 AI 비서</b> 또는 <Link href="/privacy" className="text-primary underline">개인정보처리방침</Link>.
            </p>
          </div>
        </section>

        {/* 5. WHY (간단 link) */}
        <section className="card">
          <h3 className="text-sm font-bold text-text mb-2">💡 왜 만들었나</h3>
          <p className="text-2xs text-text-sub leading-relaxed mb-2">
            대형 프랜차이즈 본사가 누리는 시스템 (트렌드 분석팀·상권 분석·실시간 데이터)을
            <b className="text-text"> 개인 카페 사장님도 같이 누렸으면</b> 좋겠어서 만들었습니다.
          </p>
          <Link href="/why" className="text-2xs text-primary font-semibold">전체 이야기 읽기 →</Link>
        </section>

        {/* 6. 회사 정보 */}
        <section className="card">
          <h3 className="text-sm font-bold text-text mb-3">🏢 회사 정보</h3>
          <dl className="grid grid-cols-[6rem_1fr] gap-y-1.5 text-2xs text-text-sub">
            <dt className="text-text font-medium">상호</dt><dd>트렌드레인</dd>
            <dt className="text-text font-medium">대표자</dt><dd>김현경</dd>
            <dt className="text-text font-medium">사업자등록</dt><dd>507-12-85992</dd>
            <dt className="text-text font-medium">통신판매업</dt><dd>제 2026-수원영통-0583 호</dd>
            <dt className="text-text font-medium">주소</dt><dd>경기도 수원시 영통구 덕영대로 1400, 207동 504호</dd>
            <dt className="text-text font-medium">고객센터</dt><dd>010-9080-8434</dd>
            <dt className="text-text font-medium">이메일</dt><dd>arsenium@naver.com</dd>
          </dl>
        </section>

        <p className="text-2xs text-text-sub text-center pt-2 print:hidden">
          가이드 v4.0 — 직원·세금·상권·트렌드 파이프라인 신규 반영 (2026-06-02)
        </p>
      </div>

      {/* PDF 인쇄 시 스타일 */}
      <style jsx global>{`
        @media print {
          html, body { background: white !important; color: black !important; }
          .card { border: 1px solid #ccc !important; background: white !important; page-break-inside: avoid; }
          .text-text, .text-text-sub, .font-bold, .font-medium { color: black !important; }
          .text-primary { color: #FF5C35 !important; }
          .bg-primary\\/15, .bg-primary\\/10, .bg-primary\\/5 { background: #FFF5F0 !important; }
          .bg-bg { background: #f5f5f5 !important; }
          nav, .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
